import React, { useMemo, useState } from 'react';
import { Pressable, PressableProps, StyleSheet, useWindowDimensions } from 'react-native';
import { DNABox, DNABoxProps, DNAIcon, DNAText, DNADivider, colors } from '@alucio/lux-ui';
import DNAPopover from 'src/components/DNA/Popover/DNAPopover';
import { useAppSettings } from 'src/state/context/AppSettings';

export const HEADER_ICON_BUTTON_BREAKPOINT = 1400;

export const shouldUseHeaderIconButtons = (width: number): boolean => {
  return width < HEADER_ICON_BUTTON_BREAKPOINT;
};

const styles = StyleSheet.create({
  toggleContainer: {
    borderRadius: 100,
    marginRight: 8,
  },
  buttonContainer: {
    height: 32,
    paddingHorizontal: 12,
    borderRadius: 100,
  },
  divider: {
    marginLeft: 8,
    marginRight: 8,
  },
});

interface SearchTypeToggleProps {
  isAiSearchActive: boolean;
  onPressSearch: () => void;
  onPressAiSearch: () => void;
  isAiSearchDisabled?: boolean;
}

interface SearchTypeButtonProps {
  isActive: boolean;
  onPress: () => void;
  label: string;
  iconName: string;
  isCompact?: boolean;
  disabled?: boolean;
  disablePopover?: boolean;
}

type SearchTypePressableProps = DNABoxProps & PressableProps;

const PressableDNABox = DNABox as React.ComponentType<SearchTypePressableProps>;

const SearchTypeButton: React.FC<SearchTypeButtonProps> = ({
  isActive,
  onPress,
  label,
  iconName,
  isCompact = false,
  disabled = false,
  disablePopover = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const backgroundColor = useMemo(() => {
    if (isActive) return colors['color-brand1-100'];
    if (isHovered) return colors['color-ui2-neutral-100'];
    return 'transparent';
  }, [isActive, isHovered]);

  const color = useMemo(() => {
    if (!isActive) return colors['color-ui2-neutral-500'];
    return colors['color-ui2-brand-600'];
  }, [isActive]);

  const popoverText = isCompact ? label : undefined;

  const button = (
    <PressableDNABox
      as={Pressable}
      onPress={onPress}
      onHoverIn={() => !isActive && !disabled && setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      disabled={disabled}
      accessibilityLabel={label}
      appearance="row"
      alignY="center"
      spacing={isCompact ? undefined : 'xs'}
      style={[
        styles.buttonContainer,
        { backgroundColor },
        isCompact && { paddingHorizontal: 8 },
      ]}
    >
      <DNAIcon.Styled
        name={iconName}
        color={color}
        width={20}
        height={20}
      />
      {!isCompact && (
        <DNAText
          style={{ color, fontSize: 14, fontWeight: '600' }}
        >
          {label}
        </DNAText>
      )}
    </PressableDNABox>
  );

  return (
    <DNAPopover disablePopover={!popoverText || disablePopover || isActive} placement="bottom">
      <DNAPopover.Anchor>
        {button}
      </DNAPopover.Anchor>
      <DNAPopover.Content>
        <DNAText colorMode="dark" numberOfLines={1}>
          {popoverText}
        </DNAText>
      </DNAPopover.Content>
    </DNAPopover>
  );
};

const SearchTypeToggle: React.FC<SearchTypeToggleProps> = ({
  isAiSearchActive,
  onPressSearch,
  onPressAiSearch,
  isAiSearchDisabled = false,
}) => {
  const { isPWAStandalone } = useAppSettings();
  const { width } = useWindowDimensions();
  const isCompact = shouldUseHeaderIconButtons(width);

  return (
    <DNABox appearance="row" alignY="center" style={styles.toggleContainer}>
      <SearchTypeButton
        label="Search"
        iconName="magnifying-glass"
        isActive={!isAiSearchActive}
        onPress={onPressSearch}
        isCompact={isCompact}
        disablePopover={isPWAStandalone}
      />
      <SearchTypeButton
        label="AI Search"
        iconName="sparkle-fill"
        isActive={isAiSearchActive}
        onPress={onPressAiSearch}
        isCompact={isCompact}
        disabled={isAiSearchDisabled}
        disablePopover={isPWAStandalone}
      />
      <DNADivider
        vertical
        height={20}
        style={StyleSheet.flatten([styles.divider, { backgroundColor: colors['color-ui2-neutral-300'] }])}
      />
    </DNABox>
  );
};

export default SearchTypeToggle;
